import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { OffsetField } from "../../components/OffsetField";
import { EtiquettesTabs } from "../../components/EtiquettesTabs";
import { printSheets, previewOf } from "../../lib/casierLabel";
import { renderRepriseLabel1, renderRepriseLabel2, emplacement, formatPrix } from "../../lib/repriseLabel";

const Field = ({ label, children }) => <label className="block text-sm">{label}{children}</label>;
const digits = (v) => v.replace(/\D/g, "");

export default function DepotRepriseLabelPage() {
  const [f, setF] = useState({ numero: "", nom: "", imei: "", prix: "", allee: "", rangee: "", tiroir: "", position: "", id: "" });
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const set = (k, fn = (v) => v) => (e) => setF((p) => ({ ...p, [k]: fn(e.target.value) }));

  const data1 = { numero: f.numero.trim(), nom: f.nom, imei: f.imei };
  const data2 = { prix: formatPrix(f.prix), emplacementTxt: emplacement(f), id: f.id };
  const has1 = !!(data1.numero || data1.nom.trim() || data1.imei);
  const has2 = !!(data2.prix || data2.emplacementTxt || data2.id);

  const previews = useMemo(() => [
    has1 && previewOf(renderRepriseLabel1(data1)).toDataURL("image/png"),
    has2 && previewOf(renderRepriseLabel2(data2)).toDataURL("image/png"),
  ], [f, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const imprimer = async () => {
    setBusy(true);
    try {
      const sheets = [];
      if (has1) sheets.push(renderRepriseLabel1(data1));
      if (has2) sheets.push(renderRepriseLabel2(data2));
      const res = await printSheets(sheets);
      if (!res.ok) toast.error(res.error || "Échec de l'impression");
      else if (window.SogoDesktop?.printImage) toast.success(`${sheets.length} étiquette(s) envoyée(s) à l'imprimante`);
    } finally { setBusy(false); }
  };

  return (
    <Layout title="Dépôt — Étiquettes">
      <EtiquettesTabs />
      <div className="max-w-2xl space-y-4" data-testid="reprise-label-page">
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div>
            <p className="text-sm font-semibold mb-2">Étiquette 1 — identification</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="N° de reprise"><Input value={f.numero} onChange={set("numero")} data-testid="rl-numero" /></Field>
              <Field label="IMEI"><Input value={f.imei} onChange={set("imei", digits)} inputMode="numeric" maxLength={17} data-testid="rl-imei" /></Field>
              <div className="col-span-2"><Field label="Nom du téléphone"><Input value={f.nom} onChange={set("nom")} placeholder="ex. iPhone 13 128 Go noir" data-testid="rl-nom" /></Field></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Étiquette 2 — prix, casier et ID</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-4 sm:w-1/2"><Field label="Prix (€)"><Input value={f.prix} onChange={set("prix")} inputMode="decimal" data-testid="rl-prix" /></Field></div>
              <Field label="Allée"><Input value={f.allee} onChange={set("allee", (v) => v.toUpperCase().slice(0, 3))} data-testid="rl-allee" /></Field>
              <Field label="Rangée"><Input value={f.rangee} onChange={set("rangee", (v) => v.toUpperCase().slice(0, 3))} data-testid="rl-rangee" /></Field>
              <Field label="Tiroir"><Input value={f.tiroir} onChange={set("tiroir", (v) => v.toUpperCase().slice(0, 3))} data-testid="rl-tiroir" /></Field>
              <Field label="Position"><Input value={f.position} onChange={set("position", (v) => v.toUpperCase().slice(0, 3))} data-testid="rl-position" /></Field>
              <div className="col-span-2 sm:col-span-4 sm:w-1/2"><Field label="ID (chiffres) — devient aussi le QR code"><Input value={f.id} onChange={set("id", digits)} inputMode="numeric" maxLength={12} data-testid="rl-id" /></Field></div>
            </div>
          </div>
          <OffsetField onChange={() => setTick((t) => t + 1)} />
        </div>

        {(has1 || has2) && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="rl-apercu">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">{has1 && has2 ? "2 étiquettes" : "1 étiquette"} (28,5 × 20 mm)</p>
              <Button onClick={imprimer} disabled={busy} className="gap-2" data-testid="rl-print"><Printer size={15} />Imprimer</Button>
            </div>
            <div className="flex flex-wrap gap-3">
              {previews.map((src, i) => src && <img key={i} src={src} alt={`Étiquette ${i + 1}`} className="w-full max-w-[260px] border border-border rounded shadow-sm bg-white" data-testid={`rl-preview-${i + 1}`} />)}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
